import { Component, inject, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  Company,
  CompanySize,
  CreateCompanyDTO,
} from 'src/app/user/model/user';
import { CompanyService } from '../../services/company.service';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { HttpClient } from '@angular/common/http';
import { User } from 'src/app/user/model/user';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { UserService } from '../../services/user.service';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { ToastModule } from 'primeng/toast';
import { NotificationService } from 'src/app/core/services/notification.service';
import { PackService } from '../../services/pack.service';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ManageCompanyPackComponent } from 'src/app/admin/components/manage-company-pack/manage-company-pack.component';
import { isValidVatFormat } from 'src/app/validators/vat.validators';
import { TranslocoModule } from '@jsverse/transloco';
import { LucideAngularModule, TriangleAlert } from 'lucide-angular';
import { LogoUploadComponent } from 'src/app/shared/components/logo-upload/logo-upload.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-edit-company',
  imports: [
    ButtonModule,
    InputTextModule,
    ReactiveFormsModule,
    FormsModule,
    SelectModule,
    RouterLink,
    ConfirmDialogModule,
    ToastModule,
    TooltipModule,
    InputNumberModule,
    CheckboxModule,
    ProgressSpinnerModule,
    ManageCompanyPackComponent,
    TranslocoModule,
    LucideAngularModule,
    LogoUploadComponent,
  ],
  templateUrl: './edit-company.component.html',
})
export class EditCompanyComponent implements OnInit {
  /** Build-time switch, mirrors FEATURE_*_ENABLED on the API. */
  protected readonly features = environment.features;
  fb = inject(FormBuilder);
  companyService = inject(CompanyService);
  activeRoute = inject(ActivatedRoute);
  notificationService = inject(NotificationService);
  confirmationService = inject(ConfirmationService);
  userService = inject(UserService);
  breadcrumbService = inject(BreadcrumbService);
  router = inject(Router);
  http = inject(HttpClient);
  packService = inject(PackService);
  company?: Company;
  userList: User[] = [];
  editMode = false;
  logoUploading = false;
  readonly alertIcon = TriangleAlert;

  /** Check if VAT format is invalid (for warning display, not blocking) */
  get vatFormatWarning(): {
    key: string;
    params?: Record<string, string>;
  } | null {
    const vat = this.companyForm.get('vatNumber')?.value;
    if (!vat || vat.length < 4) return null;
    if (isValidVatFormat(vat)) return null;
    const countryCode = vat.toUpperCase().substring(0, 2);
    if (!/^[A-Z]{2}$/.test(countryCode)) {
      return {
        key: 'accountMenu.organizationsManagement.vatFormatInvalidNoCountry',
      };
    }
    return {
      key: 'accountMenu.organizationsManagement.vatFormatInvalidForCountry',
      params: { countryCode },
    };
  }

  companyForm = this.fb.group({
    name: ['', Validators.required],
    vatNumber: [''],
    street: [''],
    streetNumber: [''],
    city: [''],
    zipCode: [''],
    country: [''],
    size: [''],
  });
  updatingRestrictedProjects = false;
  sizeOptions = Object.values(CompanySize).map((value) => ({
    labelKey: `companySize.${value}`,
    value,
  }));

  ngOnInit() {
    this.setMode();
    this.initializeForm();
  }

  setMode() {
    const id = this.activeRoute.snapshot.params['id'];
    if (id) {
      this.editMode = true;
    }
  }

  initializeForm() {
    const id = this.activeRoute.snapshot.params['id'];
    if (id) {
      this.companyService.getCompanyById(id).subscribe({
        next: (company) => {
          this.company = company;
          this.userList = company.users || [];
          this.companyForm.patchValue({
            name: company.name,
            vatNumber: company.vatNumber,
            street: company.address?.street,
            streetNumber: company.address?.streetNumber,
            city: company.address?.city,
            zipCode: company.address?.zipCode,
            country: company.address?.country,
            size: company.size,
          });

          // Set breadcrumb label
          if (company.id && company.name) {
            this.breadcrumbService.setLabel(company.id, company.name);
          }
        },
        error: (err) => console.error('Error fetching company:', err),
      });
    }
  }

  editUser(userId: string) {
    this.router.navigate(['/admin/user/edit', userId]);
  }
  confirmDelete(userId: string) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this user?',
      header: 'delete confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteUser(userId),
    });
  }

  confirmDeleteCompany() {
    this.confirmationService.confirm({
      message:
        'This action will delete all trips and users associated with this company. Are you sure you want to delete this company? ',
      header: 'delete confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteCompany(),
    });
  }

  deleteUser(userId: string) {
    this.userService.delete(userId).subscribe({
      next: (response) => {
        this.userList = this.userList.filter((user) => user.id !== response.id);
        this.notificationService.success('user successfully deleted');
      },
      error: (err) => {
        console.error('Error deleting user:', err),
          this.notificationService.error('error while deleting user');
      },
    });
  }

  deleteCompany() {
    const id = this.activeRoute.snapshot.params['id'];
    this.companyService.delete(id).subscribe({
      next: () => {
        this.notificationService.success('company successfully deleted');
        this.router.navigate(['admin/company']);
      },
      error: (err) => {
        console.error('Error deleting company:', err),
          this.notificationService.error('error while deleting company');
      },
    });
  }

  addUser() {
    this.router.navigate(['/admin/user/create'], {
      queryParams: { companyId: this.company?.id },
    });
  }

  backToList(): void {
    this.router.navigate(['/admin/company']);
  }

  submitForm() {
    if (this.companyForm.valid) {
      const formValues = this.companyForm.value;
      const data: CreateCompanyDTO = {
        ...this.company,
        name: formValues.name ?? '',
        vatNumber: formValues.vatNumber ?? '',
        size: (formValues.size as CompanySize) ?? undefined,
        address: {
          street: formValues.street ?? '',
          streetNumber: formValues.streetNumber ?? '',
          city: formValues.city ?? '',
          zipCode: formValues.zipCode ?? '',
          country: formValues.country ?? '',
        },
      };
      const id = this.activeRoute.snapshot.params['id'];
      if (id) {
        this.companyService.updateCompany(id, data).subscribe({
          next: (company) => {
            this.company = company;
            this.notificationService.success(
              'informations successfully updated',
            );
          },
          error: (err) => {
            console.error('Error updating company:', err),
              this.notificationService.error(
                'error while updating informations',
              );
          },
        });
      } else {
        this.companyService.createCompany(data).subscribe({
          next: (company) => {
            this.company = company;
            this.notificationService.success('company successfully created');
            this.router.navigate(['/admin/company/edit/' + company.id]);
          },
          error: (err) => {
            console.error('Error creating company:', err),
              this.notificationService.error('Error while creating company');
          },
        });
      }
    } else {
      console.warn('Form is invalid');
    }
  }

  onLogoCropped(logo: Blob): void {
    if (!this.company?.id) return;

    this.logoUploading = true;
    this.companyService.uploadLogo(this.company.id, logo).subscribe({
      next: ({ logoUrl }) => {
        this.company = { ...this.company, logoUrl } as Company;
        this.logoUploading = false;
        this.notificationService.success('Logo successfully updated');
      },
      error: (err) => {
        console.error('Error uploading company logo:', err);
        this.logoUploading = false;
        this.notificationService.error('Failed to upload logo');
      },
    });
  }

  toggleRestrictedProjects(value: boolean): void {
    if (!this.company?.id) return;

    this.updatingRestrictedProjects = true;
    this.companyService
      .setRestrictedProjects(this.company.id, value)
      .subscribe({
        next: (response) => {
          this.company = {
            ...this.company,
            hasRestrictedProjects: response.data.hasRestrictedProjects,
          } as Company;
          this.updatingRestrictedProjects = false;
          this.notificationService.success(
            value
              ? 'Project restriction enabled'
              : 'Project restriction disabled',
          );
        },
        error: (err) => {
          console.error('Error updating restricted projects:', err);
          this.updatingRestrictedProjects = false;
          this.notificationService.error(
            'Failed to update project restriction',
          );
        },
      });
  }
}
